import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  ELMA365NamespaceAvailability,
  ELMA365NamespaceModel,
  ELMA365NamespaceVisibility,
  ELMA365ToolsetModel,
  ELMA365VersionAction,
} from '../../../../../../gamma-api';
import { alertsSelectorAdd } from '../../../../../recoil';
import { defaultApi } from '../../../../../apis';
import IbDebounceInput from '../../../../../components/common/IbDebounceInput';
import IbPagination from '../../../../../components/common/IbPagination';
import IbTable from '../../../../../components/common/IbTable';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTextHighlighter from '../../../../../components/common/IbTextHighlighter';
import IbIcon from '../../../../../components/common/IbIcon';
import IbCheckbox from '../../../../../components/common/IbCheckbox';
import { getTranslateFunc } from '../../../../../utils/stringUtil';

import { CHECKBOX_CLASS_NAME, MAIN_CLASS_NAME, NAMESPACE_PAGE_SIZE, SEARCH_CLASS_NAME } from './const';

interface INamespaceTableProps {
  action: ELMA365VersionAction;
  toolset?: ELMA365ToolsetModel;
  versionId?: string;
  toggledIds?: { [key: string]: boolean };
  onToggle?: (id: string, selected: boolean) => void;
}

const NamespaceTable: React.FC<INamespaceTableProps> = ({ action, toolset, versionId, toggledIds, onToggle }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: NAMESPACE_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [namespaces, setNamespaces] = useState<ELMA365NamespaceModel[]>([]);

  const [loading, setLoading] = useState(true);

  const translate = getTranslateFunc('Toolkits: ELMA365: Namespaces:');

  const selectedCodes = new Set();
  if (toolset) {
    for (const namespace of toolset.namespaces) {
      selectedCodes.add(namespace.identity.code);
    }
  }

  const isSelectionDisabled = (namespace: ELMA365NamespaceModel) => {
    if (action !== ELMA365VersionAction.Appending) {
      return false;
    }
    return selectedCodes.has(namespace.identity.code);
  };

  const isNamespaceSelected = (namespace: ELMA365NamespaceModel) => {
    return toggledIds?.[namespace.namespace_id] ?? namespace.availability === ELMA365NamespaceAvailability.Enabled;
  };

  const loadNamespaces = async () => {
    if (!versionId) {
      setNamespaces([]);
      return;
    }

    setLoading(true);

    try {
      const response = await defaultApi.searchELMA365ToolkitNamespaces(
        (pagination.current || 1) - 1,
        NAMESPACE_PAGE_SIZE,
        true,
        searchText,
        undefined,
        undefined,
        [versionId],
        undefined,
        [ELMA365NamespaceVisibility.Visible]
      );
      setNamespaces(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadNamespaces().finally();
  }, [versionId, pagination.current, pagination.pageSize, searchText]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [versionId, searchText]);

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const onNamespaceToggle = (namespace: ELMA365NamespaceModel) => {
    if (isSelectionDisabled(namespace)) {
      return;
    }
    onToggle?.(namespace.namespace_id, !isNamespaceSelected(namespace));
  };

  const renderSearch = () => {
    return (
      <div className={SEARCH_CLASS_NAME}>
        <IbDebounceInput
          allowClear
          disabled={!versionId}
          placeholder={translate('Search: Placeholder')}
          prefix={<IbIcon iconName="search" />}
          onDebouncedChange={onSearchTextChange}
        />
      </div>
    );
  };

  const renderCheckbox = (namespace: ELMA365NamespaceModel) => {
    const onCheckboxChange = () => onNamespaceToggle(namespace);

    return (
      <div className={CHECKBOX_CLASS_NAME}>
        <IbCheckbox
          disabled={isSelectionDisabled(namespace)}
          value={isNamespaceSelected(namespace)}
          onChange={onCheckboxChange}
        />
        <IbTypography.Paragraph ellipsis type="secondary">
          <IbTextHighlighter highlightedText={searchText} text={namespace.identity.name} />
        </IbTypography.Paragraph>
      </div>
    );
  };

  const renderTable = () => {
    const columns: ColumnType<ELMA365NamespaceModel>[] = [
      {
        title: translate('Table: Name'),
        width: '50%',
        ellipsis: true,
        render: (_, namespace) => renderCheckbox(namespace),
      },
      {
        title: translate('Table: Code'),
        width: '50%',
        ellipsis: true,
        render: (_, namespace) => (
          <IbTypography.Paragraph ellipsis type="secondary">
            <IbTextHighlighter
              highlightedText={searchText}
              text={`${namespace.parent.code} / ${namespace.identity.code}`}
            />
          </IbTypography.Paragraph>
        ),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={namespaces}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        loading={!versionId || loading}
        rowKey={(namespace: ELMA365NamespaceModel) => namespace.namespace_id}
        size="middle"
        onRowClick={onNamespaceToggle}
      />
    );
  };

  const renderPagination = () => {
    if ((pagination.total ?? 0) <= NAMESPACE_PAGE_SIZE) {
      return null;
    }

    return (
      <IbPagination
        current={pagination.current}
        defaultCurrent={1}
        defaultPageSize={NAMESPACE_PAGE_SIZE}
        pageSize={NAMESPACE_PAGE_SIZE}
        showSizeChanger={false}
        total={pagination.total}
        onChange={onPaginationChange}
      />
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderSearch()}
      {renderTable()}
      {renderPagination()}
    </div>
  );
};

export default NamespaceTable;
